import {
  Button,
  CheckboxField,
  Flex,
  Icon,
  IconButton,
  Modal,
  useConfiguredForm,
} from '@wyz/ui';
import { uniqBy } from 'lodash-es';
import { useEffect, useEffectEvent, useMemo } from 'react';
import { Path, useFieldArray, useFormContext } from 'react-hook-form';
import { SwitchField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { IBrand } from '~/common/types';
import {
  BestSourcingBrandSelector,
  BestSourcingSupplierSelector,
} from '~/connect/components';
import {
  IAnonymizedSupplierPlatform,
  ICountry,
  ISupplierTemplateForm,
} from '~/connect/types';

type Props = {
  supplierIndex: number;
  onClose: () => void;
  brands: IBrand[];
  suppliers: IAnonymizedSupplierPlatform[];
  countries: ICountry[];
  onOpenSupplierConfiguration: () => void;
};

function BestSourcingGlobalConfiguration({
  supplierIndex,
  onClose,
  brands,
  suppliers,
  countries,
  onOpenSupplierConfiguration,
}: Props) {
  const { t } = useI18n();
  const methods = useFormContext<ISupplierTemplateForm>();
  const { mode } = useConfiguredForm();

  const baseName =
    `suppliers.${supplierIndex}.bestSourcingConfigurations` as const;

  const {
    fields: configurations,
    append,
    remove,
  } = useFieldArray({
    name: baseName,
    control: methods.control,
  });

  const segments = useMemo(() => {
    return uniqBy(
      brands.map((brand) => brand.segment),
      'id',
    ).map((segment) => ({
      label: t(segment.i18nKey),
      value: segment.publicKey,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const configurationsValues = methods.watch(baseName);

  const usedBrandIds =
    configurationsValues?.flatMap((configuration) =>
      configuration.brands.map((brand) => brand.value),
    ) ?? [];

  const usedSupplierIds = useMemo(
    () =>
      configurationsValues?.flatMap((configuration) =>
        configuration.suppliers.map((supplier) => supplier.value),
      ) ?? [],
    [configurationsValues],
  );

  const getBestSourcingValue = useEffectEvent(() => {
    return methods.getValues('bestSourcingSupplierConfigurations') || {};
  });

  const unregister = useEffectEvent((key: Path<ISupplierTemplateForm>) => {
    methods.unregister(key);
  });

  useEffect(() => {
    const validKeys = usedSupplierIds.map((id) => `supplier${id}`);
    const currentConfigs = getBestSourcingValue();

    const currentKeys = Object.keys(currentConfigs);

    const keysToRemove = currentKeys.filter((key) => !validKeys.includes(key));

    keysToRemove.forEach((key) => {
      unregister(`bestSourcingSupplierConfigurations.${key}`);
    });
  }, [usedSupplierIds]);

  return (
    <>
      <Modal.Header>
        <Flex alignItems='center' className='gap-5'>
          {mode === 'WRITE' && (
            <SwitchField
              name={`suppliers.${supplierIndex}.bestPriceDelay`}
              variant='DOUBLE'
              leftLabel={t(
                'app.supplier_template.form.best_sourcing.ranking_mode',
              )}
              rightLabel={t(
                'app.supplier_template.form.best_sourcing.best_price_delay_mode',
              )}
              onChangeValue={() =>
                configurations.forEach((_, index) =>
                  methods.setValue(`${baseName}.${index}.suppliers`, []),
                )
              }
              formGroupClassName=''
            />
          )}
          <CheckboxField
            name={`suppliers.${supplierIndex}.overridePriorityIfShorterDelivery`}
            label={
              <Trans i18nKey='app.supplier_template.form.supplier.display' />
            }
            asTextField={false}
            translate={t}
          />
          <IconButton
            id='configure-supplier'
            variant='tertiary-blue'
            icon='edit'
            onClick={onOpenSupplierConfiguration}
          >
            <Trans i18nKey='app.supplier_template.form.best_sourcing.configure_suppliers' />
          </IconButton>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <>
          <div className='space-y-4'>
            {configurations.map((configuration, index) => (
              <Flex key={configuration.id} className='gap-2'>
                <BestSourcingBrandSelector
                  name={`${baseName}.${index}.brands`}
                  brands={brands.filter(
                    (brand) => !usedBrandIds.includes(brand.publicKey),
                  )}
                  segments={segments}
                />
                <BestSourcingSupplierSelector
                  name={`${baseName}.${index}.suppliers`}
                  supplierIndex={supplierIndex}
                  suppliers={suppliers}
                  countries={countries}
                />
                {mode === 'WRITE' && (
                  <Flex
                    className='bg-light rounded gap-3 px-2'
                    direction='column'
                    alignItems='center'
                    justifyContent='center'
                    style={{ width: '3%' }}
                  >
                    <Icon
                      icon='duplicate'
                      size='md'
                      color='secondary-gray'
                      className='cursor-pointer'
                      onClick={() =>
                        append({
                          suppliers:
                            methods.getValues(`${baseName}.${index}`)
                              ?.suppliers ?? [],
                          brands: [] as { label: string; value: string }[],
                        })
                      }
                    />
                    <Icon
                      icon='trash'
                      size='md'
                      color='secondary-gray'
                      className='cursor-pointer'
                      onClick={() => remove(index)}
                    />
                  </Flex>
                )}
              </Flex>
            ))}
          </div>
          {mode === 'WRITE' && (
            <Flex justifyContent='end' className='mt-4 space-x-2'>
              <IconButton
                id='add-configuration'
                variant='primary'
                icon='add-line'
                size='sm'
                onClick={() => append({ brands: [], suppliers: [] })}
                className='text-uppercase'
              >
                <Trans i18nKey='app.supplier_template.form.best_sourcing.add_config' />
              </IconButton>
              <Button
                id='done'
                variant='secondary'
                size='sm'
                className='text-uppercase ml-2'
                onClick={onClose}
              >
                <Trans i18nKey='app.common.button.close' />
              </Button>
            </Flex>
          )}
        </>
      </Modal.Body>
    </>
  );
}

export default BestSourcingGlobalConfiguration;
